import { ContributionType, KindContribution } from "./increaseOfShareCapitalDomain";
import {
  LocalDateFieldModel,
  NumberFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { LocalizedTextEditorModel } from "@/text/localizedTextEditorDomain";
import { TMessage } from "@/i18n/tt";
import { AIClient } from "@/ai/AIAssistant";
import { Language } from "@/i18n/languages";
import { identity } from "lodash";
import { makeAutoObservable } from "mobx";
import Big from "big.js";
import { immediate } from "@/utils/functions";

export enum KindContributionType {
  currency = "currency",
  other = "other",
}

export type KindContributionMethod = OtherContribution | CurrencyContribution;

export type OtherContribution = {
  type: KindContributionType.other;
};

export type CurrencyContribution = {
  type: KindContributionType.currency;
  currency: string;
  value: number;
  exchangeRate: number;
  source: CurrencyExchangeSource;
  otherSource?: string;
  sourceDate?: string;
};

export enum CurrencyExchangeSource {
  ECB = "ECB",
  NORGES_BANK = "NORGES_BANK",
  OTHER = "OTHER",
}

export enum KindContributionOption {
  USD = "USD",
  EUR = "EUR",
  SEK = "SEK",
  DKK = "DKK",
  OTHER_CURRENCY = "otherCurrency",
  OTHER_KIND = "otherKind",
}

export class KindContributionModel {
  data?: KindContribution;

  value: NumberFieldModel;
  description: LocalizedTextEditorModel;
  address: StringFieldModel;
  totalPriceForSubscriberProvider: () => number;

  kindContributionMethod: RadioFieldModel<KindContributionOption | null>;
  currencyContribution: CurrencyKindContributionModel;

  constructor(
    aiClient: AIClient,
    documentLanguages: Language[],
    totalPriceForSubscriber: number | (() => number),
    readonly currencyContributionsInKind: KindContribution[],
    data?: KindContribution,
    address?: string,
    validateMethod?: boolean,
  ) {
    this.data = data;
    const currencyContribution = this.initDefaultValuesForCurrency();
    this.kindContributionMethod = new RadioFieldModel<KindContributionOption | null>({
      options: kindContributionMethodOptions(),
      value: toContributionKindTypeOptionValue(data?.contributionMethod),
      idFn: identity,
      labelFn: (item) =>
        item !== null
          ? kindContributionOptionsLabels[item]
          : { key: "subscriber.kindContribution.kindContributionType.select" },
      required: validateMethod,
      onChange: (value) => this.onChangeKindContributionMethod(value),
    });
    this.currencyContribution = new CurrencyKindContributionModel(
      asCurrencyContribution(data?.contributionMethod),
      asCurrencyContribution(currencyContribution),
      () => this.updateContributionValue(),
    );
    this.value = new NumberFieldModel({
      value: data?.value,
      isPositiveOrZero: true,
      required: true,
    });
    this.description = new LocalizedTextEditorModel({
      texts: data?.description,
      aiClient: aiClient,
      isReadOnly: () => false,
      label: { key: "subscriber.kindContribution.description" },
    });
    this.description.onLanguageChange(documentLanguages, data?.description);
    this.address = new StringFieldModel({ value: address, required: true });

    this.totalPriceForSubscriberProvider =
      typeof totalPriceForSubscriber === "number"
        ? () => totalPriceForSubscriber
        : totalPriceForSubscriber;
    makeAutoObservable(this, {}, { autoBind: true });

    this.updateContributionValue();
  }

  get hasSelectedMethod() {
    return this.kindContributionMethod.value !== null;
  }

  get isEmpty() {
    return this.value.toNumberOrZero() === 0;
  }

  get totalPriceForSubscriber(): number {
    return this.totalPriceForSubscriberProvider();
  }

  get hasCurrencyContributions() {
    return this.data !== undefined && this.data?.contributionMethod?.type === "currency"
      ? this.currencyContributionsInKind.length >= 2
      : this.currencyContributionsInKind.length >= 1;
  }

  initDefaultValuesForCurrency() {
    const currencyContributionInKind = this.currencyContributionsInKind[0]?.contributionMethod;
    return currencyContributionInKind
      ? asCurrencyContribution(currencyContributionInKind)
      : undefined;
  }

  defaultValuesForCurrency(currency?: string) {
    const defaultValues = currency
      ? this.currencyContributionsInKind.find(
          (kind) =>
            kind.contributionMethod?.type === "currency" &&
            kind.contributionMethod?.currency === currency,
        )
      : this.currencyContributionsInKind.find(
          (kind) =>
            toContributionKindTypeOptionValue(kind.contributionMethod) ===
            KindContributionOption.OTHER_CURRENCY,
        );
    return defaultValues ? asCurrencyContribution(defaultValues.contributionMethod) : undefined;
  }

  onChangeKindContributionMethod(value: KindContributionOption | null) {
    const currency = getCurrencyFromKindContributionTypeOption(value);
    this.currencyContribution.updateCurrency(currency, this.defaultValuesForCurrency(currency));

    if (this.isOtherKindContribution) {
      this.value.setAndCommitInputValue(this.totalPriceForSubscriber);
    } else {
      this.value.setInputValue(undefined);
    }
  }

  updateContributionValue() {
    if (this.isCurrencyContribution) {
      this.value.setAndCommitInputValue(this.currencyContribution.convertedValue);
    }
  }

  get isOtherKindContribution() {
    return this.kindContributionMethod.value === KindContributionOption.OTHER_KIND;
  }

  get isCurrencyContribution(): boolean {
    return (
      this.kindContributionMethod.value !== null &&
      this.kindContributionMethod.value !== KindContributionOption.OTHER_KIND
    );
  }

  get isOtherCurrencyContribution() {
    return this.kindContributionMethod.value === KindContributionOption.OTHER_CURRENCY;
  }

  get isKindContributionMethodValid() {
    return (
      this.kindContributionMethod.isValid &&
      (this.isOtherKindContribution || this.currencyContribution.isValid)
    );
  }

  get isValid(): boolean {
    return this.isKindContributionMethodValid && this.value.isValid && this.address.isValid;
  }

  validate(): boolean {
    this.kindContributionMethod.validate();
    if (this.isCurrencyContribution) {
      this.currencyContribution.validate();
    }
    this.value.validate();
    this.address.validate();
    return this.isValid;
  }

  toKindContribution(): KindContribution | undefined {
    return {
      type: ContributionType.kind,
      contributionMethod: immediate(() => {
        switch (this.kindContributionMethod.value) {
          case null:
            return undefined;
          case KindContributionOption.OTHER_KIND:
            return { type: KindContributionType.other };
          default:
            return this.currencyContribution.toCurrencyContribution();
        }
      }),
      description: this.description.localizedTexts,
      value: this.value.toNumber(),
    };
  }
}

export class CurrencyKindContributionModel {
  type: KindContributionType.currency;
  currency: StringFieldModel;
  value: NumberFieldModel;
  exchangeRate: NumberFieldModel;
  source: RadioFieldModel<CurrencyExchangeSource>;
  otherSource: StringFieldModel;
  sourceDate: LocalDateFieldModel;

  constructor(
    data?: CurrencyContribution,
    defaultValues?: CurrencyContribution,
    onChange?: () => void,
  ) {
    this.type = KindContributionType.currency;
    this.currency = new StringFieldModel({
      value: data?.currency ?? defaultValues?.currency,
      required: true,
    });
    this.value = new NumberFieldModel({
      value: data?.value,
      isPositive: true,
      required: true,
      onChange,
    });
    this.exchangeRate = new NumberFieldModel({
      value: data?.exchangeRate ?? defaultValues?.exchangeRate,
      isPositive: true,
      required: true,
      onChange,
    });
    this.source = new RadioFieldModel<CurrencyExchangeSource>({
      options: currencyExchangeSourceOptions(),
      value: data?.source ?? defaultValues?.source ?? CurrencyExchangeSource.ECB,
      idFn: identity,
      labelFn: (item) => currencyExchangeSourceLabels[item],
      required: true,
      onChange: (value) => this.otherSource.setRequired(value === CurrencyExchangeSource.OTHER),
    });
    this.otherSource = new StringFieldModel({
      value: data?.otherSource ?? defaultValues?.otherSource,
      required: this.source.value === CurrencyExchangeSource.OTHER,
    });
    this.sourceDate = new LocalDateFieldModel({
      value: data?.sourceDate ?? defaultValues?.sourceDate,
      required: true,
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get convertedValue(): number {
    return Big(this.value.toNumberOrZero()).times(this.exchangeRate.toNumberOrZero()).toNumber();
  }

  updateCurrency(value?: string, defaultCurrencyValues?: CurrencyContribution) {
    this.value.setInputValue(undefined);
    if (defaultCurrencyValues) {
      this.currency.setAndCommitInputValue(defaultCurrencyValues.currency);
      this.exchangeRate.setAndCommitInputValue(defaultCurrencyValues.exchangeRate);
      this.source.setValue(defaultCurrencyValues.source);
      this.otherSource.setAndCommitInputValue(defaultCurrencyValues?.otherSource ?? "");
      this.sourceDate.setAndCommitInputValue(defaultCurrencyValues.sourceDate);
    } else {
      if (value !== undefined) {
        this.currency.setAndCommitInputValue(value);
      } else {
        this.currency.setInputValue("");
      }
      this.exchangeRate.setInputValue(undefined);
    }
  }

  toCurrencyContribution(): CurrencyContribution {
    return {
      type: this.type,
      currency: this.currency.validatedValue,
      value: this.value.toNumberOrZero(),
      exchangeRate: this.exchangeRate.toNumberOrZero(),
      source: this.source.value,
      otherSource: this.otherSource.validatedValue,
      sourceDate: this.sourceDate.toISOString(),
    };
  }

  get isValid(): boolean {
    return (
      this.currency.isValid &&
      this.value.isValid &&
      this.exchangeRate.isValid &&
      this.source.isValid &&
      this.otherSource.isValid &&
      this.sourceDate.isValid
    );
  }

  validate(): boolean {
    this.currency.validate();
    this.value.validate();
    this.exchangeRate.validate();
    this.source.validate();
    this.otherSource.validate();
    this.sourceDate.validate();
    return this.isValid;
  }
}

function asCurrencyContribution(data?: KindContributionMethod): CurrencyContribution | undefined {
  if (data?.type === KindContributionType.currency) {
    return data;
  }
}

export function kindContributionMethodOptions(): (KindContributionOption | null)[] {
  return [null, ...Object.values(KindContributionOption)];
}

export function toContributionKindTypeOptionValue(
  contributionMethod?: KindContributionMethod,
): KindContributionOption | null {
  if (contributionMethod === undefined) {
    return null;
  } else if (contributionMethod.type === KindContributionType.other) {
    return KindContributionOption.OTHER_KIND;
  } else {
    switch (contributionMethod.currency) {
      case "USD":
        return KindContributionOption.USD;
      case "EUR":
        return KindContributionOption.EUR;
      case "SEK":
        return KindContributionOption.SEK;
      case "DKK":
        return KindContributionOption.DKK;
      default:
        return KindContributionOption.OTHER_CURRENCY;
    }
  }
}

export function getCurrencyFromKindContributionTypeOption(
  value: KindContributionOption | null,
): string | undefined {
  switch (value) {
    case KindContributionOption.DKK:
      return "DKK";
    case KindContributionOption.USD:
      return "USD";
    case KindContributionOption.EUR:
      return "EUR";
    case KindContributionOption.SEK:
      return "SEK";
    default:
      return undefined;
  }
}

export const currencyExchangeSourceOptions = () => Object.values(CurrencyExchangeSource);

export const currencyExchangeSourceLabels: { [key in CurrencyExchangeSource]: TMessage } = {
  [CurrencyExchangeSource.ECB]: { key: "subscriber.kindContribution.exchangeSource.ECB" },
  [CurrencyExchangeSource.NORGES_BANK]: { translated: "Norges Bank" },
  [CurrencyExchangeSource.OTHER]: { key: "subscriber.kindContribution.exchangeSource.other" },
};

export const kindContributionOptionsLabels: { [key in KindContributionOption]: TMessage } = {
  [KindContributionOption.USD]: { translated: "USD" },
  [KindContributionOption.EUR]: { translated: "EUR" },
  [KindContributionOption.SEK]: { translated: "SEK" },
  [KindContributionOption.DKK]: { translated: "DKK" },
  [KindContributionOption.OTHER_CURRENCY]: {
    key: "subscriber.kindContribution.kindContributionType.otherCurrency",
  },
  [KindContributionOption.OTHER_KIND]: {
    key: "subscriber.kindContribution.kindContributionType.otherKind",
  },
};
